import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import CustomModal from '../ui/modal/modal.component';
import { useAuthContext, useHasPermission } from '../../contexts/auth';
import { useDemo } from '../../api/demo';
import { ERROR_SNACKBAR } from '../../consts';
import { requestTutorialAfterDemoLoad } from '../tutorial/tutorial.context';

interface DemoOfferModalProps {
  /** The first-login prompts before this one are out of the way. */
  ready: boolean;
}

/**
 * The last of the first-login prompts: offers a workspace on trial to try the
 * app on demo data. Offered once per workspace, to someone who may change it -
 * remembered on the backend - and only while the workspace is still empty;
 * the Workspace page keeps the button for later.
 */
export const DemoOfferModal = ({ ready }: DemoOfferModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, setCurrentUser } = useAuthContext();
  const canManage = useHasPermission('app.change_tenant');
  const { query, start, markOffered, loading } = useDemo();
  const [step, setStep] = useState<'closed' | 'offer' | 'declined'>('closed');

  const tenant = currentUser?.tenant;
  const due =
    ready &&
    canManage &&
    !!tenant &&
    tenant.subscription?.status === 'T' &&
    tenant.demo_status === 'N' &&
    !tenant.demo_offered_at;

  useEffect(() => {
    if (!due || !currentUser || !tenant) return;

    let cancelled = false;
    (async () => {
      const state = await query();
      if (cancelled || !state) return;
      // Shown now, or pointless (the workspace already has records) - either
      // way it is never offered again.
      setCurrentUser({
        ...currentUser,
        tenant: { ...tenant, demo_offered_at: new Date().toISOString() },
      });
      markOffered();
      if (state.can_start) setStep('offer');
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [due]);

  const handleStart = async () => {
    const { success, errorData } = await start();
    if (!success) {
      enqueueSnackbar(
        errorData?.message || t('Could not load the demo data. Please try again.'),
        ERROR_SNACKBAR
      );
      return;
    }
    setStep('closed');
    requestTutorialAfterDemoLoad();
    // The current user now reports the demo loading, which brings up the
    // loading screen (DemoBanner).
    window.dispatchEvent(new Event('tenantUpdated'));
  };

  const offer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2">
        {t('We can fill your workspace with a made-up copier service: clients, equipment, contracts, a year of settlements and invoices, and service jobs. It is the quickest way to see what Copigo can do.')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('Nothing leaves the workspace while the demo is in it: e-mails go only to your workspace’s users, nothing is sent to KSeF and no courier is ordered. When you are ready to start for real, clear the demo data with one click.')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={() => setStep('declined')} disabled={loading}>
          {t('No, start with an empty workspace')}
        </Button>
        <Button variant="contained" onClick={handleStart} loading={loading}>
          {t('Load demo data')}
        </Button>
      </Box>
    </Box>
  );

  const declined = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2">
        {t('No problem. You can still load demo data at any time during your trial - from Workspace, on the Overview tab.')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={() => setStep('closed')}>
          {t('Got it')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <CustomModal
      opened={step !== 'closed'}
      // Closing the offer is declining it, which still deserves the pointer to
      // where it can be taken up later.
      setOpened={(value) => {
        if (value || loading) return;
        setStep(step === 'offer' ? 'declined' : 'closed');
      }}
      title={step === 'declined' ? t('Demo data') : t('See Copigo in action?')}
      width="min(600px, 92vw)"
      minWidth="min(600px, 92vw)"
      minHeight="auto"
      content={step === 'declined' ? declined : offer}
    />
  );
};
